import { Download } from '@repo/go-icons';
import { Button, Checkbox, DatePickerV2, Modal } from '@repo/go-ui';
import { minutesToSeconds } from 'date-fns';
import { observer } from 'mobx-react-lite';
import { useTranslation } from 'react-i18next';

import { hasValue } from 'src/shared/utils/common';

import type { ExportStore } from './export-store';
import { fixDatePickerTime } from './export-store';
import styles from './export.module.scss';

type Props = {
  exportStore: ExportStore;
};

export const ExportModal = observer<Props>(function ExportModal({ exportStore }) {
  const { t } = useTranslation();
  const {
    viewData: { startDate, endDate, wellList, gantt, wellPadPreparations },
    isModalOpen,
    error,
    updateData,
    setIsModalOpen,
    resetModalData,
  } = exportStore;

  const onCancel = () => {
    if (!error) {
      resetModalData();
    }

    setIsModalOpen(false);
  };

  return (
    <Modal open={isModalOpen} title={t('drillingCarpet:Export.title')} onClose={() => setIsModalOpen(false)} onClickOutside={null}>
      <div className={styles.wrapper}>
        <div className={styles.inputsWrapper}>
          <div className={styles.intervalWrapper}>
            <p className={styles.intervalLabel}>{t('drillingCarpet:Export.period')}</p>
            <div className={styles.intervalInputsWrapper}>
              <DatePickerV2
                placeholder={t('drillingCarpet:Export.dateFrom')}
                className={styles.datePicker}
                value={startDate}
                allowClear={false}
                onChange={(startDate) => {
                  if (!hasValue(startDate)) {
                    return;
                  }

                  updateData({ startDate });
                }}
              />
              —
              <DatePickerV2
                placeholder={t('drillingCarpet:Export.dateTo')}
                className={styles.datePicker}
                value={endDate}
                isDateDisabled={(date) => {
                  const dateInst = new Date(date * 1000);
                  const timezoneOffset = dateInst.getTimezoneOffset();

                  const fixedDate = fixDatePickerTime(date - minutesToSeconds(timezoneOffset));

                  return fixedDate < startDate;
                }}
                onChange={(endDate) => updateData({ endDate })}
              />
            </div>
          </div>

          {exportStore.isWellListAvailable && (
            <label className={styles.tagWrapper}>
              <Checkbox checked={wellList} onChange={(checked) => updateData({ wellList: checked })} />
              <p className={styles.tagLabel}>{t('drillingCarpet:Export.wellList')}</p>
            </label>
          )}

          {exportStore.isGanttAvailable && (
            <label className={styles.tagWrapper}>
              <Checkbox
                checked={gantt}
                disabled={exportStore.isPaletteSettingsLoading || !exportStore.currentPaletteObjectType}
                onChange={(checked) => updateData({ gantt: checked })}
                loading={exportStore.isPaletteSettingsLoading}
              />
              <p className={styles.tagLabel}>{t('drillingCarpet:Export.gantt')}</p>
            </label>
          )}

          {exportStore.isWPPAvailable && (
            <label className={styles.tagWrapper}>
              <Checkbox
                checked={wellPadPreparations}
                onChange={(checked) => updateData({ wellPadPreparations: checked })}
              />
              <p className={styles.tagLabel}>{t('drillingCarpet:Export.wellPadPreparation')}</p>
            </label>
          )}
        </div>

        <div className={styles.buttonsWrapper}>
          <Button variant="danger" className={styles.button} onClick={onCancel}>
            {t('common:Buttons.cancel')}
          </Button>
          <Button variant="success" className={styles.button} icon={<Download />} onClick={exportStore.export}>
            {t('common:exportAction')}
          </Button>
        </div>
      </div>
    </Modal>
  );
});
